// Pointer, touch and keyboard on the webtop: right-click for touch (a two-finger press) and pen (a long press),
// arranging (a long press on the webtop with a finger: tiles jiggle, move and resize), dragging tiles (and dropping them
// onto groups; a corner's grip resizes instead, client/lib/resize.js), the selection box (mouse, or two fingers), keys on a tile (open, delete, nudge), and Cmd / Ctrl+A
// and Delete / Backspace for the selected tiles wherever the focus is.
import { WIDGETS } from '../widgets/index.js';
import { SCALE } from '../widgets/kit.js';
import { dragPreview, dropArranged, dropRoom, endPreview, roomPreview } from './arrange.js';
import { autoOn, clamp, fitX, moveTo, pinDrawn, place, placeTile, settle, sizeOf, snap, snapOn, spotOf, tilePos, viewportOn } from './geometry.js';
import { groupUnder, joinGroup } from './groups.js';
import { render, selectedShortcuts, setSelection } from './render.js';
import { $menu } from './menu.js';
import { cancelResize, resizing, startResize } from './resize.js';
import { removeShortcuts } from './shortcuts.js';
import { API, byId, currentRole, $webtop, selected } from './state.js';
import { notchAtBottom, overTrash, showTrash } from './notch.js';
import { GROUPABLE, onWebtop, openShortcut } from './tiles.js';
import { api, toast, TYPING } from './ui.js';

export const DRAG_THRESHOLD = 5;
// Parts of a widget that take pointer and key events for themselves: they never drag, select, nudge or delete tiles.
const FIELDS = ['textarea, input, button', ...Object.values(WIDGETS).map((w) => w.fields).filter(Boolean)].join(', ');
const EDGE_SCROLL = 48; // px from the screen's top or bottom where a dragged tile scrolls the webtop
const EDGE_SCROLL_STEP = 12; // px scrolled per frame there
const MOUSE_SCROLL = 5; // a mouse's bottom band: it can reach the very edge, and a wider band scrolled on the way to the notch's trash
const NOTCH_SCROLL = 24; // under Viewport a finger's bottom band also starts this far above the notch, which a phone's nearly fills
const PAGE_MAX = 100_000; // px the page grows to at most while a tile is dragged down (the model's POS_MAX is about the same)
const $marquee = document.getElementById('marquee');

// Touch and pen have no right-click: holding still for LONG_PRESS_MS fires a contextmenu event at the element
// under the finger, so every menu (webtop, tiles, notch, launcher) opens the same way it does on right-click. On an
// editable webtop a finger's long press starts arranging instead, and its menus open with two fingers: pressing two
// fingers down (a tap, or holding them still for LONG_PRESS_MS) opens the menu of whatever the first one is on, and
// that works everywhere. The press then neither opens the tile, nor drags it, nor clicks. Browsers that raise their
// own contextmenu on a long press (Android) are ignored for touch, so the menu opens once.
const LONG_PRESS_MS = 500;
const LONG_PRESS_SLOP = 10; // px of finger movement that still counts as holding still
let longPress = null; // the pending press: {id, x, y, timer}
export let longPressed = false; // set when a press became a menu or started arranging; cleared by that touch's click or the next press
export let lastPointerType = 'mouse';
function cancelLongPress(e) {
  if (longPress?.id !== e.pointerId) return;
  clearTimeout(longPress.timer);
  longPress = null;
}
const fingers = new Map(); // pointerId -> {x, y, target}: where each finger now down touched, for the two-finger press
let twoFinger = null; // {first, timer} while two fingers rest; first is the first finger's entry in fingers
function endTwoFinger() {
  clearTimeout(twoFinger?.timer);
  twoFinger = null;
}
function twoFingerMenu() {
  const { x, y, target } = twoFinger.first;
  endTwoFinger();
  openMenu(target, x, y);
}
function openMenu(target, x, y) {
  longPressed = true;
  navigator.vibrate?.(10);
  target.dispatchEvent(new MouseEvent('contextmenu', { bubbles: true, cancelable: true, clientX: x, clientY: y, button: 2 }));
}

// Arranging (#140): a finger on a tile only opens it or scrolls the page, so a touch never moves a tile by accident.
// A long press on the webtop (a tile or empty space) starts arranging: the tiles jiggle and one finger drags them, or a
// corner of one to resize it, starting with the press that began it, and a tap selects a tile instead of opening it. A tap on empty space, a long
// press on a tile (a full webtop may have no empty space), Escape or another webtop ends it. The mouse drags tiles as it always has; a pen drags them while arranging, which it
// starts from the webtop menu (its long press is the menu).
export let arranging = false;
export function arrange(on) {
  arranging = on;
  $webtop.classList.toggle('arranging', on);
}

// Touch selection. When two fingers touch the empty webtop, their spots are pinned. The fingers' current positions
// are two more spots, and the box is the smallest rectangle holding all four: drag down from two side-by-side
// fingers and the box grows down from them; move the fingers back and it shrinks, but never past the pinned spots.
// Tiles the box touches are selected; the box is only drawn once a finger moves, since two fingers held still are a
// menu. Lifting a finger finishes it (the selection stays). While two fingers are down, tiles are neither dragged nor
// opened. Without a second finger, a one-finger tap on empty space clears it (and ends arranging).
const touches = new Map(); // pointerId -> {x, y, x0, y0}: fingers down on the empty webtop
let selBox = null; // {pins: [{x, y}, {x, y}], l, t, w, h, rects, scrollY, live} while two fingers draw a box; 'done' until all lift
function tileRects() {
  return onWebtop().map((s) => {
    const r = $webtop.querySelector(`.tile[data-id="${s.id}"]`).getBoundingClientRect();
    return { id: s.id, l: r.left, t: r.top, r: r.right, b: r.bottom };
  });
}
function touchDown(e) {
  try { $webtop.setPointerCapture(e.pointerId); } catch {}
  if (selBox) return; // a third finger, or a new one before the others lifted, does nothing
  touches.set(e.pointerId, { x: e.clientX, y: e.clientY, x0: e.clientX, y0: e.clientY });
  if (touches.size < 2) return;
  const [p, q] = [...touches.values()];
  selBox = { pins: [{ x: p.x, y: p.y }, { x: q.x, y: q.y }], rects: tileRects(), scrollY, live: false };
}
// The box around the two pinned spots and the fingers' current positions.
function fitSelBox() {
  const pts = [...selBox.pins, ...touches.values()];
  const xs = pts.map((p) => p.x), ys = pts.map((p) => p.y);
  selBox.l = Math.min(...xs); selBox.t = Math.min(...ys);
  selBox.w = Math.max(...xs) - selBox.l; selBox.h = Math.max(...ys) - selBox.t;
}
function drawSelBox() {
  const { l, t, w, h } = selBox;
  const r = l + w, bt = t + h;
  Object.assign($marquee.style, { left: `${l}px`, top: `${t}px`, width: `${w}px`, height: `${h}px` });
  $marquee.hidden = false;
  setSelection(selBox.rects.filter((q) => q.l < r && q.r > l && q.t < bt && q.b > t).map((q) => q.id));
}
function touchUp(e) {
  const f = touches.get(e.pointerId);
  if (!f) return;
  touches.delete(e.pointerId);
  if (selBox && selBox !== 'done') { selBox = 'done'; $marquee.hidden = true; }
  else if (!selBox && e.type === 'pointerup' && !longPressed && Math.hypot(f.x - f.x0, f.y - f.y0) < DRAG_THRESHOLD) {
    setSelection([]);
    arrange(false);
  }
  if (!touches.size) selBox = null;
}

let tileTouch = null; // the touch press on a tile in progress: {dragging(), abort()}
let dropDrag = null; // ends the drag in progress without dropping anything (cancelDrag)
// Ends a tile drag without a drop, for client/lib/nav.js when another webtop is opened mid-drag: the dragged elements are
// about to go, and with them the pointer capture whose pointerup would have tidied up.
export function cancelDrag() { dropDrag?.(); cancelResize(); }
function startTileDrag(e, tile) {
  if (e.target.closest(FIELDS)) return; // typing or selecting text inside a widget, or reading a note
  const s = byId(tile.dataset.id);
  if (!s) return;
  e.preventDefault();
  const multi = e.ctrlKey || e.metaKey; // Shift is kept for snapping while dragging
  const pointer = e.pointerType;
  let group = null, tiles = new Map(), origin = null;
  // Focuses the tile and selects it for a drag. A finger or pen outside arranging waits for the drag (a long press
  // can start one): its press is more likely a scroll, which must leave the tile as it found it.
  const take = () => {
    if (group) return;
    tile.focus();
    if (multi) setSelection(selected.has(s.id) ? [...selected].filter((id) => id !== s.id) : [...selected, s.id]);
    else if (!selected.has(s.id)) setSelection([s.id]);
    group = selectedShortcuts().filter((x) => selected.has(x.id));
    tiles = new Map(group.map((x) => [x.id, $webtop.querySelector(`.tile[data-id="${x.id}"]`)]));
    origin = new Map(group.map((x) => [x.id, tilePos(tiles.get(x.id))]));
  };
  if (pointer === 'mouse' || arranging) take();
  const start = { px: e.clientX, py: e.clientY, st: scrollY };
  let dragging = false;
  let moved = false;
  let lastMove = null, edgeFrame = 0;
  let overBin = false; // the pointer is over the notch's trash: a drop deletes the dragged tiles
  tile.setPointerCapture(e.pointerId);
  dropDrag = () => {
    dropDrag = null;
    tileTouch = null;
    cancelAnimationFrame(edgeFrame);
    tile.removeEventListener('pointermove', move);
    tile.removeEventListener('pointerup', end);
    tile.removeEventListener('pointercancel', end);
    tiles.forEach((t) => t.classList.remove('dragging'));
    showTrash(false);
    endPreview();
  };
  const dropped = () => new Map(group.map((x) => [x.id, tilePos(tiles.get(x.id))])); // where the dragged tiles are
  if (e.pointerType === 'touch') {
    let last = { x: e.clientX, y: e.clientY };
    tile.addEventListener('pointermove', (ev) => { if (ev.pointerId === e.pointerId) last = { x: ev.clientX, y: ev.clientY }; });
    tileTouch = {
      dragging: () => dragging,
      abort: () => { // stop this press without opening or moving the tile; report where the finger is
        tile.removeEventListener('pointermove', move);
        tile.removeEventListener('pointerup', end);
        tile.removeEventListener('pointercancel', end);
        tileTouch = null;
        return { pointerId: e.pointerId, ...last };
      },
    };
  }

  const follow = (ev) => { // draws the dragged tiles under the pointer
    const dx = ev.clientX - start.px, dy = ev.clientY - start.py + scrollY - start.st;
    for (const [id, t] of tiles) {
      t.classList.add('dragging');
      const o = origin.get(id);
      // Shift snaps to the grid while it is held, whatever the webtop's own setting.
      const size = sizeOf(byId(id));
      placeTile(t, ev.shiftKey ? snap(o.x + dx / SCALE / fitX(), o.y + dy / SCALE, size, true, true) : clamp(o.x + dx / SCALE / fitX(), o.y + dy / SCALE, size, viewportOn() ? 'open' : 'view'));
    }
    if (autoOn()) dragPreview(dropped());
    else if (viewportOn() && snapOn()) roomPreview(dropped(), ev, overBin);
  };
  // Held near the top or bottom of the screen, a dragged tile scrolls a webtop that reaches below it. Under Viewport
  // the page grows ahead of it, so a tile can be taken further down than anything is yet; the next render() sets the
  // height back to what the tiles need (scrollEnd).
  const edgeScroll = () => {
    edgeFrame = 0;
    const y = lastMove.clientY;
    // The notch may sit in the bottom band, so holding a tile over its trash must not scroll the webtop.
    const own = viewportOn();
    const low = pointer === 'mouse' ? innerHeight - MOUSE_SCROLL
      : own && notchAtBottom() ? Math.min(innerHeight - EDGE_SCROLL, document.getElementById('notch').getBoundingClientRect().top - NOTCH_SCROLL)
        : innerHeight - EDGE_SCROLL;
    const dir = overBin ? 0 : y < EDGE_SCROLL ? -1 : y > low ? 1 : 0;
    if (own && dir > 0) $webtop.style.setProperty('--webtop-h', `${Math.min(scrollY + innerHeight + EDGE_SCROLL_STEP, PAGE_MAX)}px`);
    const before = scrollY;
    if (dir) scrollBy(0, dir * EDGE_SCROLL_STEP);
    if (scrollY === before) return;
    follow(lastMove);
    edgeFrame = requestAnimationFrame(edgeScroll);
  };
  const move = (ev) => {
    const held = pointer !== 'mouse' && arranging; // a finger or pen drags only while arranging
    if (longPressed && !held) return; // the press became a menu, not a drag
    const dx = ev.clientX - start.px, dy = ev.clientY - start.py;
    if (!dragging && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
    moved = true; // past the threshold: no longer a tap
    if (currentRole() !== 'editor') return; // a locked webtop: tiles open on tap but do not move
    if (pointer !== 'mouse' && !held) return; // not arranging: the finger scrolls the page instead
    if (!dragging) { take(); showTrash(true); }
    dragging = true;
    lastMove = ev;
    overBin = overTrash(ev.clientX, ev.clientY);
    follow(ev);
    if (!edgeFrame) edgeFrame = requestAnimationFrame(edgeScroll);
  };
  const end = async (ev) => {
    dropDrag = null;
    tileTouch = null;
    cancelAnimationFrame(edgeFrame);
    tile.removeEventListener('pointermove', move);
    tile.removeEventListener('pointerup', end);
    tile.removeEventListener('pointercancel', end);
    tiles.forEach((t) => t.classList.remove('dragging'));
    const binned = dragging && ev.type === 'pointerup' && overTrash(ev.clientX, ev.clientY);
    showTrash(false);
    endPreview();
    if (binned) return removeShortcuts(group, { ask: false }); // dropped on the trash: deleted, no confirmation
    if (dragging) {
      // Shortcuts dropped onto a group join it.
      const target = groupUnder(ev.clientX, ev.clientY, new Set(group.map((x) => x.id)));
      if (target && group.every((x) => GROUPABLE.includes(x.kind))) return joinGroup(target, group);
      if (autoOn()) return dropArranged(dropped());
      if (viewportOn() && snapOn()) return dropRoom(dropped());
      // Dropped tiles settle on the nearest spot free of the other tiles (as drawn) and of each other; that spot
      // is saved, since the user put them there. Then everything is laid out again around them. Widgets come
      // first and only have to clear other widgets: one dropped onto shortcuts stays where it was dropped, and the
      // layout moves the shortcuts out of its way (as it always lets widgets go before shortcuts).
      const ids = new Set(group.map((x) => x.id));
      const taken = [...$webtop.querySelectorAll('.tile')].filter((t) => !ids.has(t.dataset.id)).map((t) => {
        const s = byId(t.dataset.id);
        return { ...tilePos(t), ...sizeOf(s), widget: !!WIDGETS[s.kind] };
      });
      const updates = [];
      const ordered = [...group].sort((a, b) => !!WIDGETS[b.kind] - !!WIDGETS[a.kind]);
      for (const x of ordered) {
        const t = tiles.get(x.id);
        const at = tilePos(t), size = sizeOf(x), widget = !!WIDGETS[x.kind];
        // Off the grid, a drop that only just overlaps a tile slides clear of it rather than jumping a cell.
        const free = !ev.shiftKey && !snapOn();
        // Under Viewport no other tile may move, so a widget has to clear them all too.
        const spot = snap(at.x, at.y, size, ev.shiftKey, true);
        const p = viewportOn() ? settle(spot, taken, size, free)
          : place(spot, widget ? taken.filter((q) => q.widget) : taken, size, false, null, free);
        taken.push({ ...p, ...size, widget });
        updates.push(moveTo(x, p));
      }
      updates.push(...pinDrawn(ordered));
      render(ordered);
      try {
        if (updates.length === 1 && !updates[0].band) await api('PATCH', `${API}/shortcuts/${updates[0].id}`, { x: updates[0].x, y: updates[0].y });
        else await api('PATCH', `${API}/shortcuts`, updates);
      } catch (err) { toast(err); }
    } else if (ev.type === 'pointerup' && !multi && !longPressed && !moved) {
      const shows = pointer === 'mouse' || arranging; // a finger's tap opens the tile and leaves no selection behind
      setSelection(shows ? [s.id] : []);
      if (!shows) tile.focus({ preventScroll: true }); // what shows a widget's controls that have no hover here
      if (pointer === 'mouse' || !arranging) openShortcut(s); // while arranging, a tap only selects
    }
  };
  tile.addEventListener('pointermove', move);
  tile.addEventListener('pointerup', end);
  tile.addEventListener('pointercancel', end);
}

// Rubber-band selection on the empty background. The box is kept in page coordinates (screen + scrollY), so it stays
// anchored to the webtop while the page scrolls: by the wheel, or by holding the pointer near the top or bottom edge,
// which scrolls a webtop that reaches below the screen like a dragged tile does.
function startMarquee(e) {
  const additive = e.ctrlKey || e.metaKey || e.shiftKey;
  const base = additive ? [...selected] : [];
  const start = { x: e.clientX, y: e.clientY + scrollY };
  let active = false;
  let last = { x: e.clientX, y: e.clientY }, edgeFrame = 0;
  $webtop.setPointerCapture(e.pointerId);

  const rects = onWebtop().map((s) => {
    const r = $webtop.querySelector(`.tile[data-id="${s.id}"]`).getBoundingClientRect();
    return { id: s.id, l: r.left, t: r.top + scrollY, r: r.right, b: r.bottom + scrollY };
  });

  const draw = () => {
    const y = last.y + scrollY;
    const l = Math.min(start.x, last.x), t = Math.min(start.y, y);
    const r = Math.max(start.x, last.x), b = Math.max(start.y, y);
    // The marquee is position: fixed, so it is drawn in screen coordinates.
    Object.assign($marquee.style, { left: `${l}px`, top: `${t - scrollY}px`, width: `${r - l}px`, height: `${b - t}px` });
    $marquee.hidden = false;
    const hit = rects.filter((q) => q.l < r && q.r > l && q.t < b && q.b > t).map((q) => q.id);
    setSelection([...new Set([...base, ...hit])]);
  };
  const edgeScroll = () => {
    edgeFrame = 0;
    const dir = last.y < EDGE_SCROLL ? -1 : last.y > innerHeight - EDGE_SCROLL ? 1 : 0;
    const before = scrollY;
    if (dir) scrollBy(0, dir * EDGE_SCROLL_STEP);
    if (scrollY === before) return;
    draw();
    edgeFrame = requestAnimationFrame(edgeScroll);
  };
  const move = (ev) => {
    last = { x: ev.clientX, y: ev.clientY };
    if (!active && Math.hypot(ev.clientX - start.x, ev.clientY + scrollY - start.y) < DRAG_THRESHOLD) return;
    active = true;
    draw();
    if (!edgeFrame) edgeFrame = requestAnimationFrame(edgeScroll);
  };
  const scrolled = () => { if (active) draw(); };
  const end = () => {
    cancelAnimationFrame(edgeFrame);
    $webtop.removeEventListener('pointermove', move);
    $webtop.removeEventListener('pointerup', end);
    $webtop.removeEventListener('pointercancel', end);
    removeEventListener('scroll', scrolled);
    $marquee.hidden = true;
    if (!active && !additive) setSelection([]); // plain click on the background clears the selection
  };
  $webtop.addEventListener('pointermove', move);
  $webtop.addEventListener('pointerup', end);
  $webtop.addEventListener('pointercancel', end);
  addEventListener('scroll', scrolled, { passive: true });
}

// Arrow keys move tiles at once and save once the keys rest (300 ms). The save keeps the webtop it was made on, and
// client/lib/nav.js flushes it before opening another webtop, so neither the switch nor a nudge there drops it.
let nudgeTimer;
let nudged = null; // {at, updates}: the moves since the last save, and their webtop's API
export function flushNudge() {
  clearTimeout(nudgeTimer);
  if (!nudged) return;
  const { at, updates } = nudged;
  nudged = null;
  api('PATCH', `${at}/shortcuts`, updates).catch(toast);
}

// A finger that keeps flicking the page must not get stuck in a widget that scrolls inside itself when it lands on one.
// A browser picks what a swipe scrolls as the finger lands, so whatever scrolls in a widget stops doing so while the page
// moves, and for PAGE_SCROLL_MS after, not when the finger comes down: that would be too late on iOS. The scrollers are
// measured, so an applet's own need nothing, and a swipe that starts with the page at rest scrolls the widget as ever.
// A widget's drawings take no events then either (`.page-moving`, style.css), so a chart shows no values for a passing
// finger. Links and buttons stay live, so a tap right after the page stops still works.
const PAGE_SCROLL_MS = 300;
let heldScrollers = null; // [{el, was}] while the page is moving
let pageScrollTimer = 0;
function holdWidgetScrollers() {
  heldScrollers = [...$webtop.querySelectorAll('.tile.widget *')]
    .filter((el) => el.scrollHeight > el.clientHeight && el !== document.activeElement && /auto|scroll/.test(getComputedStyle(el).overflowY))
    .map((el) => ({ el, was: el.style.overflowY }));
  for (const { el } of heldScrollers) el.style.overflowY = 'hidden';
  $webtop.classList.add('page-moving');
}
function pageScrolled() {
  if (lastPointerType === 'mouse') return;
  if (!heldScrollers) holdWidgetScrollers();
  clearTimeout(pageScrollTimer);
  pageScrollTimer = setTimeout(() => {
    for (const { el, was } of heldScrollers) el.style.overflowY = was;
    heldScrollers = null;
    $webtop.classList.remove('page-moving');
  }, PAGE_SCROLL_MS);
}

// Event wiring and first drawing, run once by client.js in the order the parts of the page depend on.
export function init() {
  addEventListener('scroll', pageScrolled, { passive: true });
  // Older iOS Safari still pinch-zooms despite the viewport meta and touch-action; its gesture events can stop it.
  for (const t of ['gesturestart', 'gesturechange']) addEventListener(t, (e) => e.preventDefault(), { passive: false });
  addEventListener('pointerdown', (e) => {
    if (e.pointerType === 'touch') {
      if (e.isPrimary) { fingers.clear(); endTwoFinger(); } // a new touch: nothing left over from the last one
      fingers.set(e.pointerId, { x: e.clientX, y: e.clientY, target: e.target });
      endTwoFinger();
      if (fingers.size === 2) twoFinger = { first: fingers.values().next().value, timer: setTimeout(twoFingerMenu, LONG_PRESS_MS) };
    }
    lastPointerType = e.pointerType;
    longPressed = false;
    clearTimeout(longPress?.timer);
    longPress = null;
    if (e.pointerType === 'mouse' || !e.isPrimary) return;
    if (e.target === document.activeElement && e.target.matches('textarea, input')) return; // editing: the browser's own press-and-hold
    const { pointerId: id, clientX: x, clientY: y, target } = e;
    longPress = {
      id, x, y,
      timer: setTimeout(() => {
        longPress = null;
        if (e.pointerType !== 'touch' || !$webtop.contains(target) || currentRole() !== 'editor') return openMenu(target, x, y);
        longPressed = true;
        if (tileTouch?.dragging() || resizing()) return; // holding a tile still in the middle of moving it
        navigator.vibrate?.(10);
        const tile = target.closest('.tile');
        if (!arranging || !tile) return arrange(true);
        tile.blur();
        setSelection([]);
        arrange(false);
      }, LONG_PRESS_MS),
    };
  }, true);
  addEventListener('pointermove', (e) => {
    const f = fingers.get(e.pointerId);
    if (twoFinger && f && Math.hypot(e.clientX - f.x, e.clientY - f.y) > LONG_PRESS_SLOP) endTwoFinger(); // a box or a scroll
    if (longPress?.id === e.pointerId && Math.hypot(e.clientX - longPress.x, e.clientY - longPress.y) > LONG_PRESS_SLOP) cancelLongPress(e);
  }, true);
  addEventListener('pointerup', (e) => {
    cancelLongPress(e);
    if (fingers.delete(e.pointerId) && twoFinger) twoFingerMenu(); // a two-finger tap
  }, true);
  addEventListener('pointercancel', (e) => {
    cancelLongPress(e);
    fingers.delete(e.pointerId);
    endTwoFinger();
  }, true);
  addEventListener('click', (e) => {
    if (!longPressed) return;
    longPressed = false;
    e.preventDefault();
    e.stopPropagation();
  }, true);
  addEventListener('contextmenu', (e) => {
    if (e.isTrusted && lastPointerType !== 'mouse') { e.preventDefault(); e.stopImmediatePropagation(); }
  }, true);

  $webtop.addEventListener('pointerdown', (e) => {
    if (e.button !== 0) return;
    if (currentRole() !== 'editor') { // locked: tiles open on tap, nothing is selected by box or touch
      const tile = e.target.closest('.tile');
      if (tile) startTileDrag(e, tile);
      return;
    }
    if (resizing()) return; // another finger during a resize does nothing
    // A finger joining one already on the empty webtop starts (or belongs to) a selection: tiles are not dragged.
    if (e.pointerType === 'touch' && touches.size) return touchDown(e);
    // The same when the first finger is resting on a tile without having dragged it yet: that press becomes the
    // selection's first spot instead.
    if (e.pointerType === 'touch' && tileTouch && !tileTouch.dragging()) {
      const f = tileTouch.abort();
      touches.set(f.pointerId, { x: f.x, y: f.y, x0: f.x, y0: f.y });
      return touchDown(e);
    }
    const tile = e.target.closest('.tile');
    const grip = e.target.closest('.tile-grip');
    if (grip && (e.pointerType === 'mouse' || arranging)) return startResize(e, tile, grip.dataset.corner);
    if (tile) return startTileDrag(e, tile);
    if (e.target !== $webtop) return;
    if (e.pointerType === 'touch') touchDown(e); // one finger does nothing here, two start a selection box
    else startMarquee(e);
  });
  $webtop.addEventListener('pointermove', (e) => {
    const f = touches.get(e.pointerId);
    if (!f) return;
    f.x = e.clientX; f.y = e.clientY;
    if (!selBox || selBox === 'done') return;
    if (!selBox.live && (longPressed || Math.hypot(f.x - f.x0, f.y - f.y0) <= LONG_PRESS_SLOP)) return; // still a press
    selBox.live = true;
    fitSelBox();
    drawSelBox();
  });
  $webtop.addEventListener('pointerup', touchUp);
  $webtop.addEventListener('pointercancel', touchUp);
  // #webtop is touch-action: pan-y so one finger scrolls the webtop, which also lets the browser scroll (and on iOS
  // zoom) under a two-finger selection box: the page jolts, and once the browser takes over the gesture it cancels
  // the pointers and ends the box. While a box is being drawn, or its fingers are still down, the touches are kept
  // from the browser (pointerdown comes before touchstart, so selBox is already set for the finger that starts it),
  // and a scroll that started anyway (momentum from the first finger) is held where the box began. The listeners
  // must not be passive, or preventDefault is ignored. The same goes for the finger whose long press on a tile started
  // arranging: its tile was still pan-y when it touched down, so its moves are kept from the browser to drag the tile.
  const holdTouches = (e) => {
    const dragged = e.type === 'touchmove' && arranging && e.target.closest?.('.tile') && !e.target.closest(FIELDS);
    if ((selBox || dragged) && e.cancelable) e.preventDefault();
  };
  $webtop.addEventListener('touchstart', holdTouches, { passive: false });
  $webtop.addEventListener('touchmove', holdTouches, { passive: false });
  addEventListener('scroll', () => { if (selBox && selBox !== 'done' && scrollY !== selBox.scrollY) scrollTo(0, selBox.scrollY); });
  addEventListener('keydown', (e) => { if (e.key === 'Escape' && arranging) arrange(false); });
  $webtop.addEventListener('keydown', (e) => {
    if (e.target.closest(FIELDS)) return; // keys belong to the field being typed in
    const tile = e.target.closest('.tile');
    if (!tile) return;
    const s = byId(tile.dataset.id);
    if (!s) return;
    if (e.key === 'Escape') return setSelection([]);
    if (e.key === 'Enter') return openShortcut(s);
    if (currentRole() !== 'editor' && (e.key === 'Delete' || e.key === 'Backspace' || e.key.startsWith('Arrow'))) return; // locked: no moving or deleting
    if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault(); // handled: the window handler below must not ask a second time
      return removeShortcuts(selected.has(s.id) && selected.size > 1 ? selectedShortcuts() : [s]);
    }
    const step = e.shiftKey ? 1 : 8;
    const d = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key];
    if (!d) return;
    e.preventDefault();
    const group = selected.has(s.id) ? selectedShortcuts() : [s];
    // Under Viewport a tile moves from where it is drawn (it may have no spot for this screen yet) and may go below it.
    const own = viewportOn();
    const updates = group.map((x) => {
      const t = $webtop.querySelector(`.tile[data-id="${x.id}"]`);
      const from = own ? tilePos(t) : x;
      const p = clamp(from.x + d[0], from.y + d[1], sizeOf(x), own ? 'open' : 'screen');
      const update = moveTo(x, p);
      placeTile(t, p);
      t.scrollIntoView({ block: 'nearest' });
      return update;
    });
    updates.push(...pinDrawn(group));
    clearTimeout(nudgeTimer);
    nudged = { at: API, updates };
    nudgeTimer = setTimeout(flushNudge, 300);
  });
  // Cmd / Ctrl+A anywhere on an unlocked webtop selects every tile on it (group members stay in their group), instead
  // of the browser selecting the page's text. In a text field, or with a menu, dialog, the launcher or a group panel
  // open, the browser keeps it.
  addEventListener('keydown', (e) => {
    if (e.key?.toLowerCase() !== 'a' || !(e.ctrlKey || e.metaKey) || e.altKey || e.shiftKey) return;
    if (e.defaultPrevented || e.target.closest?.(TYPING) || ($webtop.contains(e.target) && e.target.closest(FIELDS))) return;
    if (!$menu.hidden || document.querySelector('dialog[open], .launcher:not([hidden]), #group-panel:not([hidden])')) return;
    if (currentRole() !== 'editor') return;
    e.preventDefault();
    setSelection(onWebtop().map((x) => x.id));
  });
  // Delete / Backspace with tiles selected but no tile focused (a selection box, or a click on the webtop or the notch
  // after selecting) deletes them like the Delete menu item: after the confirmation. Keys typed into a field, or with
  // a menu, dialog, the launcher or a group panel open, are left alone, and a locked webtop has no selection.
  addEventListener('keydown', (e) => {
    if (e.key !== 'Delete' && e.key !== 'Backspace') return;
    if (e.defaultPrevented || e.isComposing || e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.target.closest?.(TYPING) || ($webtop.contains(e.target) && e.target.closest(FIELDS))) return;
    if (!$menu.hidden || document.querySelector('dialog[open], .launcher:not([hidden]), #group-panel:not([hidden])')) return;
    const list = selectedShortcuts();
    if (!list.length || currentRole() !== 'editor') return;
    e.preventDefault();
    removeShortcuts(list);
  });
}
